import { Injectable } from '@angular/core';
import { languages } from '../../../constants/language.constants';
import { Country, CountryDetails, countriesByLanguage } from './countries.config';
import countryCodes from '../../../../assets/countries/phone-codes.json';

const LEADING_PLUS_OR_ZEROS_PATTERN = new RegExp('^[+|00]+');
const WHITE_SPACES_PATTERN = new RegExp('\\s+');

@Injectable({
  providedIn: 'root'
})
export class CountriesService {
  countriesByLanguage = countriesByLanguage;
  curLangCountriesObj: Country;
  countries: CountryDetails[] = [];
  countryCodesMap = {};
  countryCodeMaxLength: number;
  constructor() {
    this.initCountryCodeMap();
    this.initCountryCodeMaxLength();
  }

  initCountries(lang: languages): CountryDetails[] {
    const curLanguage = lang || languages.en;

    this.curLangCountriesObj = countriesByLanguage[curLanguage];
    this.countries = Object.keys(this.curLangCountriesObj).map((key) => {
      return { value: key, ...this.curLangCountriesObj[key] };
    });

    return this.sortByName(this.countries);
  }

  getCountryName(countryCode) {
    const country = this.curLangCountriesObj[countryCode];
    if (country) {
      return country.name;
    }
    return '';
  }

  getCountryByLocalizedName(localizedCountryName: string): string | undefined {
    const englishCountries = this.countriesByLanguage[languages.en];
    const germanCountries = this.countriesByLanguage[languages.de];
    const spanishCountries = this.countriesByLanguage[languages.es];

    const countryResult = Object.keys(englishCountries).find((countryCode) => {
      const countryNames = [
        englishCountries[countryCode]?.name,
        germanCountries[countryCode]?.name,
        spanishCountries[countryCode]?.name,
        englishCountries[countryCode]?.nationality,
        germanCountries[countryCode]?.nationality,
        spanishCountries[countryCode]?.nationality
      ]
        .filter(Boolean)
        .map(this.cleanString);
      return countryNames.includes(this.cleanString(localizedCountryName));
    });

    return countryResult;
  }

  getNationalityName(nationalityCode) {
    const country = this.curLangCountriesObj[nationalityCode];
    if (country) {
      return country.nationality ? country.nationality : country.name;
    }
    return '';
  }

  sortByName(countries): CountryDetails[] {
    return countries.sort((a, b) => a.name.localeCompare(b.name));
  }

  compareNationality(nationalities: CountryDetails[]) {
    return nationalities.sort((a, b) =>
      (a.nationality ? a.nationality : a.name).localeCompare(b.nationality ? b.nationality : b.name)
    );
  }

  parsePhoneNumber(numberStr: string) {
    if (!numberStr) {
      return {
        countryCode: '',
        nationalNumber: ''
      };
    }
    let nationalNumber = numberStr.replace(LEADING_PLUS_OR_ZEROS_PATTERN, '').replace(WHITE_SPACES_PATTERN, '');
    let countryCode = '';
    const startIdx = Math.min(this.countryCodeMaxLength, nationalNumber.length - 1);
    for (let idx = startIdx; idx > 0 && idx < nationalNumber.length; idx--) {
      const possibleCountryCode = `+${nationalNumber.substring(0, idx)}`;
      if (this.countryCodesMap[possibleCountryCode]) {
        countryCode = possibleCountryCode;
        nationalNumber = nationalNumber.substring(idx);
        break;
      }
    }
    return {
      countryCode,
      nationalNumber
    };
  }

  private initCountryCodeMap() {
    this.countryCodesMap = {};
    Object.keys(countryCodes).forEach((key) => {
      this.countryCodesMap[countryCodes[key]] = key;
    });
  }

  private initCountryCodeMaxLength() {
    this.countryCodeMaxLength = 0;
    Object.keys(countryCodes).forEach((key) => {
      if (countryCodes[key].length > this.countryCodeMaxLength) {
        this.countryCodeMaxLength = countryCodes[key].length;
      }
    });
  }

  private cleanString(string: string): string {
    return string.toLowerCase().trim().replace(/ /g, '');
  }
}
